import { useEffect, useState } from "react";
import { Button, Alert } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { httpApiPostText } from "../../../../lib";
import { FaTrash, FaSearch } from "react-icons/fa";
import API_HOST, { HEADERS_JSON } from "../../../../ApiConfig";
import ConfirmModal from "../../../../components/ConfirmModal";

const PER_PAGE    = 10;
const MIN_CHARS   = 3;
const DEBOUNCE_MS = 400;

const FormularioPropiedadesClientes = (props: any) => {
  const [form, setForm]                   = useState({ texto: "" });
  const [data, setData]                   = useState<any[]>([]);
  const [searchInput, setSearchInput]     = useState("");
  const [searchActive, setSearchActive]   = useState("");
  const [currentPage, setCurrentPage]     = useState(1);
  const [totalRows, setTotalRows]         = useState(0);
  const [loading, setLoading]             = useState(false);
  const [errorMsg, setErrorMsg]           = useState("");   // mensaje de error visible

  const [confirm, setConfirm] = useState({
    show: false, mensaje: "", variante: "danger" as "danger"|"warning", accion: () => {}
  });
  const pedirConfirm = (msg: string, fn: () => void, v: "danger"|"warning" = "danger") =>
    setConfirm({ show: true, mensaje: msg, variante: v, accion: fn });
  const cerrarConfirm = () => setConfirm(c => ({ ...c, show: false }));

  // ── Carga paginada ────────────────────────────────────────────────────────
  const cargar = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const trimmed = search.trim();
      const usePaged = trimmed.length >= MIN_CHARS || page > 1;
      const body: any = usePaged
        ? { valid: "selectClientsPaged", search: trimmed, page, per_page: PER_PAGE }
        : { valid: "selectClients" };
      const res = await httpApiPostText(API_HOST, "POST", HEADERS_JSON, body);
      if (!res?.message) return;
      const raw = res.message as string;
      const dta = JSON.parse(raw.substring(raw.indexOf("{")));
      if (dta?.payload) {
        setData(dta.payload);
        setTotalRows(dta.total ?? dta.payload.length);
        setCurrentPage(page);
      }
    } catch (e) {
      console.error("Error cargando clientes:", e);
    } finally {
      setLoading(false);
    }
  };

  // ── Debounce búsqueda ─────────────────────────────────────────────────────
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchActive) return;
    if (trimmed.length > 0 && trimmed.length < MIN_CHARS) return;
    const timer = setTimeout(() => {
      setSearchActive(trimmed);
      cargar(1, trimmed);
    }, trimmed.length === 0 ? 0 : DEBOUNCE_MS);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handlePageChange = (page: number) => cargar(page, searchActive);

  // ── Columnas ──────────────────────────────────────────────────────────────
  const columns = [
    { name: "Id", selector: (row: any) => row.id, sortable: true, width: "70px" },
    { name: "Descripción", selector: (row: any) => row.description, sortable: true, grow: 10 },
    {
      name: "Estado", width: "110px",
      cell: (row: any) => (
        <span
          className={`badge ${row.status === "activo" ? "bg-success" : "bg-secondary"}`}
          style={{ cursor: "pointer", fontSize: "12px", padding: "6px 10px" }}
          title="Click para cambiar estado"
          onClick={() => pedirConfirm(
            `¿Deseas cambiar el estado a ${row.status === "activo" ? "inactivo" : "activo"}?`,
            () => cambiarEstado(row.id), "warning"
          )}
        >
          {row.status === "activo" ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      name: "Acciones", width: "90px", center: true,
      cell: (row: any) => (
        <button type="button" className="btn btn-sm p-0" style={{ background: "none", border: "none" }}
          title="Eliminar cliente"
          onClick={(e) => {
            e.stopPropagation();
            pedirConfirm(
              `¿Seguro que deseas eliminar el cliente "${row.description}"? Esta acción no se puede deshacer.`,
              () => eliminar(row.id)
            );
          }}>
          <FaTrash color="white" className="bg-danger rounded-circle p-1" style={{ fontSize: "26px" }} />
        </button>
      ),
    },
  ];

  const pagOptions = { rowsPerPageText: "Filas por páginas", rangeSeparatorText: "de", selectAllRowsItem: true, selectAllRowsItemText: "Todos" };
  const conditionalRowStyles = [{ when: (row: any) => row.status === "inactivo", style: { backgroundColor: "#D6D5D5" } }];

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const onTextChange = (e: any) => {
    setErrorMsg("");
    setForm(f => ({ ...f, [e.target.id]: e.target.value }));
  };

  const onButtonClickAgregar = async (event: any) => {
    event.preventDefault();
    if (form.texto.trim() === "") return;
    setErrorMsg("");
    try {
      const res = await httpApiPostText(API_HOST, "POST", HEADERS_JSON,
        { valid: "newClients", description: form.texto.trim() }
      );
      const raw = res.message as string;
      const dta = JSON.parse(raw.substring(raw.indexOf("{")));
      if (dta.code === 2) {
        // Duplicado — mostrar error inline sin limpiar el input
        setErrorMsg(`Ya existe un cliente con el nombre "${form.texto.trim()}"`);
        return;
      }
      if (dta.code === 1) {
        setForm({ texto: "" });
        cargar(1, searchActive);
      }
    } catch (e) {
      console.error("Error al agregar cliente:", e);
    }
  };

  const cambiarEstado = async (id: any) => {
    await httpApiPostText(API_HOST, "POST", HEADERS_JSON, { valid: "changeClientStatus", id });
    cargar(currentPage, searchActive);
  };

  const eliminar = async (id: any) => {
    try {
      await httpApiPostText(API_HOST, "POST", HEADERS_JSON, { valid: "deleteClient", id });
    } catch (e) { console.error("Error al eliminar cliente:", e); }
    cargar(currentPage, searchActive);
  };

  useEffect(() => { cargar(1, ""); }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid p-0">
      <div className="row mb-2">
        <div className="col col-lg-10">
          <label htmlFor="texto" className="form-label">Descripción</label>
          <input
            type="text"
            className={`form-control ${errorMsg ? "is-invalid" : ""}`}
            id="texto"
            onChange={onTextChange}
            value={form.texto}
            onKeyDown={(e) => { if (e.key === "Enter" && form.texto.trim()) onButtonClickAgregar(e); }}
          />
          {errorMsg && (
            <div className="invalid-feedback d-block">
              ⚠️ {errorMsg}
            </div>
          )}
        </div>
        <div className="col-lg-2 d-flex align-items-start mt-4">
          <Button type="button" className="bg-dark w-100" onClick={onButtonClickAgregar}>
            Agregar
          </Button>
        </div>
      </div>

      <div className="mb-2" style={{ position: "relative", maxWidth: "360px" }}>
        <div className="input-group">
          <span className="input-group-text bg-white"><FaSearch size={13} color="#888" /></span>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar (mín. 3 caracteres)..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button className="btn btn-outline-secondary" type="button"
              onClick={() => setSearchInput("")}>✕</button>
          )}
        </div>
        {searchInput.length > 0 && searchInput.trim().length < MIN_CHARS && (
          <small className="text-muted" style={{ position: "absolute", bottom: "-17px", left: "4px", fontSize: "11px" }}>
            Escribe al menos {MIN_CHARS} caracteres
          </small>
        )}
      </div>

      <div className="mt-3">
        <p>Listado de Clientes</p>
        <DataTable
          columns={columns}
          data={data}
          conditionalRowStyles={conditionalRowStyles}
          selectableRowsHighlight
          fixedHeader
          pagination
          paginationServer={searchInput.trim().length >= MIN_CHARS}
          paginationTotalRows={searchInput.trim().length >= MIN_CHARS ? totalRows : undefined}
          onChangePage={handlePageChange}
          paginationPerPage={PER_PAGE}
          paginationDefaultPage={currentPage}
          highlightOnHover
          paginationComponentOptions={pagOptions}
          progressPending={loading}
          progressComponent={<div className="p-3 text-muted">Cargando...</div>}
          noDataComponent={<div className="p-3 text-muted">No hay registros</div>}
        />
      </div>

      <ConfirmModal
        show={confirm.show}
        mensaje={confirm.mensaje}
        variante={confirm.variante}
        onConfirm={() => { cerrarConfirm(); confirm.accion(); }}
        onCancel={cerrarConfirm}
      />
    </div>
  );
};

export default FormularioPropiedadesClientes;